import * as THREE from 'three';
import { BLACK, emis, std } from '../materials.ts';
import { M, V, glowSprite } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * La Bebedora (El pozo 2, docs/SAGAS.md): a leech of ink the size of a cart, its mouth stuck to the vein under
 * Musgoviejo's well. A long body of soft rings, black and wet with a bruise-violet sheen, tapering to a blunt tail;
 * at the front a round lamprey mouth ringed with pale hooked teeth, no eyes. Empty it is thin and wrinkled, its
 * rings slack; swollen (`full`) the middle rings bulge round and go thin-skinned, and the water it drank shows through,
 * a pale cold light sloshing in its belly: the moment to hit it. The rings swell and ease in a wave along it (its four
 * boil frames).
 */

const RINGS = 9;

export const drinker = (o?: BaseOpts & { full?: boolean }): Model => {
  const c = begin(o, 1.1, 77);
  const { g } = c;
  const full = !!o?.full;
  const skin = std('#0d0b14', { roughness: 0.22, metalness: 0.25, bumpScale: 0.05 });
  const skinD = std('#07060b', { roughness: 0.3, metalness: 0.2, bumpScale: 0.06 });
  const sheen = std('#2a1f3a', { roughness: 0.2, metalness: 0.3 });
  const tooth = std('#cfc8b6', { roughness: 0.5 });
  const gum = emis('#3a1626', '#5a1a30', 0.6);
  const water = emis('#6f9ab8', '#5a9ad0', full ? 0.55 : 0.2);
  const thin = std('#141a26', { roughness: 0.12, metalness: 0.15, transparent: true, opacity: 0.72 });
  const sphere = new THREE.SphereGeometry(0.5, 14, 10);
  // Along +Z, the mouth at the front; each ring's girth (empty, then swollen in the middle).
  const rings: THREE.Mesh[] = [];
  const base: number[] = [];
  const zs: number[] = [];
  for (let i = 0; i < RINGS; i++) {
    const k = i / (RINGS - 1);
    const z = -1.3 + k * 2.5;
    // Fattest a little behind the middle, thin at the tail, blunt at the head.
    const shape = Math.sin(Math.PI * Math.min(1, k * 1.08 + 0.04)) ** 0.7;
    const belly = full ? 1 + 0.75 * Math.exp(-(((k - 0.45) / 0.22) ** 2)) : 1;
    const girth = (0.32 + 0.42 * shape) * belly * (full ? 1 : 0.86);
    const mat = full && belly > 1.3 ? thin : i % 2 ? skin : skinD;
    const m = M(sphere, mat, g);
    m.position.set(0, girth * 0.82, z);
    m.scale.set(girth * 2, girth * 1.65, 0.5);
    rings.push(m);
    base.push(girth);
    zs.push(z);
    // Empty: slack folds across each ring; swollen, a sheen line along the back.
    if (!full) M(new THREE.TorusGeometry(girth * 0.92, 0.035, 5, 16), sheen, g).position.set(0, girth * 0.82, z + 0.14);
    // The water inside the swollen rings, glowing through the thin skin.
    if (full && belly > 1.15) {
      const w = M(sphere, water, g);
      w.position.set(0, girth * 0.8, z);
      w.scale.set(girth * 1.5, girth * 1.2, 0.38);
    }
  }
  // A wet violet stripe down its back.
  for (let i = 1; i < RINGS - 1; i++) {
    const s = M(new THREE.BoxGeometry(0.08, 0.04, 0.32), sheen, g);
    s.position.set(0, base[i]! * 0.82 * 2 - 0.02, zs[i]!);
  }
  // The mouth: a round lip ringed with hooked teeth, a dark gullet, pointing a little down.
  const head = new THREE.Group();
  head.position.set(0, base[RINGS - 1]! * 0.82, 1.32);
  head.rotation.x = 0.35;
  g.add(head);
  M(new THREE.TorusGeometry(0.3, 0.09, 8, 18), gum, head);
  M(new THREE.CircleGeometry(0.28, 16), BLACK, head).position.z = -0.02;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const t = M(new THREE.ConeGeometry(0.035, 0.14, 4), tooth, head);
    t.position.set(Math.cos(a) * 0.24, Math.sin(a) * 0.24, 0.02);
    t.rotation.set(Math.sin(a) * 0.9, -Math.cos(a) * 0.9, 0);
    t.rotateX(Math.PI / 2);
  }
  glowSprite(head, '#5a1a30', 0.6, V(0, 0, 0.05), 0.5);
  if (full) glowSprite(g, '#9fd8ff', 1.3, V(0, 0.9, -0.1), 0.22);
  // A peristaltic wave along the rings.
  const tick = (n: number): void => {
    rings.forEach((m, i) => {
      const w = 1 + (full ? 0.05 : 0.09) * Math.sin(n * (Math.PI / 2) - i * 0.9);
      const gi = base[i]!;
      m.scale.set(gi * 2 * w, gi * 1.65 * w, 0.5);
    });
    head.rotation.x = 0.35 + 0.08 * Math.sin(n * (Math.PI / 2));
  };
  return done(c, full ? 1.9 : 1.4, tick);
};
